// #6093: measure the time the page was actually RUNNING, not the time on the wall clock.
//
// When a phone puts the app in the background or locks the screen, the operating system
// stops running the page's JavaScript. No timer runs while the page is frozen, but the wall
// clock keeps moving. On waking, every timer whose due time has passed fires at once, so a
// time limit that was armed before the freeze "expires" in the very first moment the page
// runs again — before the thing it guards has had any chance to answer. In production that
// reported a stuck boot that finished in the same second, and rebuilt a database connection
// that was working (design-docs/boot-sequence.md, "#6093").
//
// The fix is to count only running time. A page that is running reads its clock often, so
// every gap between two reads is short. A frozen page shows up as ONE gap that is far longer
// than the code ever waits between reads. So each read counts the gap since the previous read
// only up to a cap, and sets the rest aside as time the page was not running.
//
// Why gaps rather than `visibilitychange`: a hidden event does not arrive before every
// freeze, and a gap is there whatever the cause. Why not restart the limit on waking: a
// truly stuck operation could then dodge its limit for ever through quick background flips
// (#4232 rejected that for the same reason); here the total running budget stays fixed.
//
// The cost, accepted: a main thread busy with synchronous work for longer than the cap is
// also counted as "not running". A real stall is still caught once it has used its full
// running budget afterwards.
//
// Keep this module free of Angular and of browser globals at load time: its unit test runs
// it under plain node with a clock and timers driven by hand.

import { TimeoutError } from '@cf-shared/shared-util';

/**
 * A clock that advances only while the page runs.
 *
 * `read()` adds the time since the previous read, but at most `maxCreditMs` of it. The rest
 * goes to `notRunningMs()`. The caller must read it more often than `maxCreditMs` while the
 * page runs normally, or ordinary running time is cut off too — every user in this app reads
 * it from a repeating timer whose interval is well under its cap.
 */
export class RunningClock {
  private lastReadAtMs: number;
  private runningMs = 0;
  private skippedMs = 0;

  // `nowMs` must be a monotonic clock in production (`performance.now()`): the wall clock can
  // be stepped by the operating system, which would create or hide a gap that never happened.
  constructor(private readonly nowMs: () => number, private readonly maxCreditMs: number) {
    this.lastReadAtMs = nowMs();
  }

  /** Running milliseconds since this clock was created. Every call advances it. */
  read(): number {
    const now = this.nowMs();
    const gap = now - this.lastReadAtMs;
    this.lastReadAtMs = now;
    // if the clock did not move forward, there is nothing to count
    if (gap > 0) {
      const counted = Math.min(gap, this.maxCreditMs);
      this.runningMs += counted;
      this.skippedMs += gap - counted;
    }
    return this.runningMs;
  }

  /** Milliseconds set aside as "the page was not running", as of the last `read()`. */
  notRunningMs(): number {
    return this.skippedMs;
  }
}

/** The clock and timers a running-time limit uses. Injected so tests can drive them by hand. */
export interface TimerEnvironment {
  nowMs: () => number;
  setTimeout: (cb: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

/** How often a running-time limit checks its clock. */
export const RUNNING_TIME_TICK_MS = 1_000;

/**
 * The most one tick may count, however long it really took. Five ticks' worth, so a page that
 * is merely slow (a long boot task delaying the tick by a few seconds) still has its time
 * counted, while a freeze of minutes counts as five seconds.
 */
export const RUNNING_TIME_MAX_TICK_CREDIT_MS = 5_000;

/** A `RunningClock` kept ticking beside some other work; see `startRunningClockTicks`. */
export interface RunningClockTicks {
  readonly clock: RunningClock;
  /**
   * Stop ticking and take one last reading. Returns the running time at that moment. Safe to
   * call twice: a second call changes nothing and returns the same value.
   */
  stop(): number;
}

/**
 * Start a `RunningClock` and keep reading it every `RUNNING_TIME_TICK_MS` until `stop()`, so a
 * freeze shows up as one long gap. For the wrappers that only MEASURE beside a wall-time limit
 * or an operation; `scheduleRunningTimeTimeout` keeps its own loop, because its tick IS the
 * limit (it fires the callback and shortens the last wait).
 *
 * `stop()` reads the clock once more because, on waking, a failure or a wall-time timer can
 * run before the overdue tick does, and the freeze would otherwise go uncounted.
 */
export function startRunningClockTicks(env: TimerEnvironment): RunningClockTicks {
  const clock = new RunningClock(env.nowMs, RUNNING_TIME_MAX_TICK_CREDIT_MS);
  let tickTimer: unknown = undefined;
  let stoppedAtRunningMs: number | null = null;

  // one tick: read the clock, then re-arm unless stopped
  function tick() {
    tickTimer = undefined;
    if (stoppedAtRunningMs !== null) return;
    clock.read();
    tickTimer = env.setTimeout(tick, RUNNING_TIME_TICK_MS);
  }

  tickTimer = env.setTimeout(tick, RUNNING_TIME_TICK_MS);
  return {
    clock,
    stop() {
      // if already stopped, keep the reading taken then
      if (stoppedAtRunningMs !== null) return stoppedAtRunningMs;
      if (tickTimer !== undefined) env.clearTimeout(tickTimer);
      tickTimer = undefined;
      stoppedAtRunningMs = clock.read();
      return stoppedAtRunningMs;
    },
  };
}

/** What `scheduleRunningTimeTimeout` returns; hand it to `clearRunningTimeTimeout`. */
export interface RunningTimeTimeoutHandle {
  cancel(): void;
  /** Time set aside as "the page was not running" so far, as of the limit's last tick (#6079). */
  notRunningMs(): number;
}

/**
 * Like `setTimeout(cb, ms)`, except `ms` is RUNNING time: time the page spent frozen counts
 * for at most `RUNNING_TIME_MAX_TICK_CREDIT_MS`.
 *
 * It re-arms a short tick and asks a `RunningClock` how much running time has passed. Only
 * one real timer is ever pending, so cancelling costs one `clearTimeout`.
 */
export function scheduleRunningTimeTimeout(
  cb: () => void,
  ms: number,
  env: TimerEnvironment,
): RunningTimeTimeoutHandle {
  const clock = new RunningClock(env.nowMs, RUNNING_TIME_MAX_TICK_CREDIT_MS);
  let pendingTimer: unknown = undefined;
  let finished = false;

  // one tick: count the running time since the last tick, then fire or wait one more tick
  function tick() {
    pendingTimer = undefined;
    if (finished) return;
    const remainingMs = ms - clock.read();
    // if the running budget is used up, fire once; otherwise check again after one more tick
    if (remainingMs <= 0) {
      finished = true;
      cb();
      return;
    }
    pendingTimer = env.setTimeout(tick, Math.min(RUNNING_TIME_TICK_MS, remainingMs));
  }

  pendingTimer = env.setTimeout(tick, Math.min(RUNNING_TIME_TICK_MS, Math.max(0, ms)));
  return {
    cancel() {
      finished = true;
      if (pendingTimer !== undefined) env.clearTimeout(pendingTimer);
      pendingTimer = undefined;
    },
    notRunningMs: () => clock.notRunningMs(),
  };
}

/** Cancel a limit made by `scheduleRunningTimeTimeout`. Safe to call twice, or after it fired. */
export function clearRunningTimeTimeout(handle: unknown): void {
  if (handle && typeof (handle as RunningTimeTimeoutHandle).cancel === 'function') {
    (handle as RunningTimeTimeoutHandle).cancel();
  }
}

/** The real clock and timers. Read lazily, so importing this module touches no browser global. */
export function realTimerEnvironment(): TimerEnvironment {
  return {
    nowMs: () => performance.now(),
    setTimeout: (cb, ms) => setTimeout(cb, ms),
    clearTimeout: handle => clearTimeout(handle as ReturnType<typeof setTimeout>),
  };
}

// #6096: a limit that STAYS on wall time, but can say afterwards whether a freeze caused it.
//
// Some limits guard a request to the server. When the phone froze the app mid-request, giving
// up the moment it wakes is right: a connection that was open across a long freeze is often
// dead, and the code behind these limits starts a fresh request straight away. What was wrong
// was only the REPORT, which counted such a give-up as a hang. So the limit keeps its wall-time
// timer, and a running-time clock ticks beside it purely to classify the timeout.
// design-docs/boot-sequence.md, "#6096".

/** What a wall-time limit that fired because of a freeze knows about it. */
export interface TimeoutAfterFreeze {
  /** Time the page was frozen while the limit ran, so it was not running. */
  notRunningMs: number;
}

/**
 * Whether a freeze caused a wall-time limit of `limitMs` to fire, given the running time a
 * `RunningClock` read at that moment. A page that kept running has run for the whole limit by
 * then; one that froze has more than one tick of its running budget unused. Every wall-time
 * limit that classifies its timeout uses this one rule, so they all agree on what a freeze is.
 */
export function limitFiredBecauseOfFreeze(limitMs: number, runningMsAtFire: number): boolean {
  return limitMs - runningMsAtFire > RUNNING_TIME_TICK_MS;
}

/** The error `timeoutNotingFreezeAsync` rejects with: a `TimeoutError` plus what the clock saw. */
type TimeoutErrorWithRunningTime = TimeoutError & { runningTimeFacts?: { freezeCaused: boolean, notRunningMs: number } };

/**
 * Like `timeout(ms, operation)` from shared-util, on the same wall time and with the same
 * `TimeoutError`, except the error also records whether the page was frozen for so much of
 * the limit that a freeze caused it. Read that with `timeoutCausedByFreeze`.
 *
 * A freeze caused it when, at the moment the wall limit fired, the page had run for more than
 * one tick less than the limit. A page that kept running has run for the whole limit by then.
 */
export function timeoutNotingFreezeAsync<T>(
  ms: number,
  operation: Promise<T>,
  env: TimerEnvironment = realTimerEnvironment(),
): Promise<T> {
  const ticks = startRunningClockTicks(env);
  let finished = false;
  let limitTimer: unknown = undefined;

  // stop the ticks and the limit; only the first outcome counts
  function finish(): boolean {
    if (finished) return false;
    finished = true;
    ticks.stop();
    if (limitTimer !== undefined) env.clearTimeout(limitTimer);
    limitTimer = undefined;
    return true;
  }

  return new Promise<T>((resolve, reject) => {
    limitTimer = env.setTimeout(() => {
      limitTimer = undefined;
      if (!finish()) return;
      // finish() took the last reading, at the moment the limit fired
      const runningMs = ticks.stop();
      const error = new TimeoutError(ms) as TimeoutErrorWithRunningTime;
      error.runningTimeFacts = {
        freezeCaused: limitFiredBecauseOfFreeze(ms, runningMs),
        notRunningMs: ticks.clock.notRunningMs(),
      };
      reject(error);
    }, ms);
    operation.then(
      value => { if (finish()) resolve(value); },
      err => { if (finish()) reject(err); },
    );
  });
}

// df-c1ad475aa8f5: the same question for a failure that is NOT our own timer. On iOS a request
// open across a freeze fails on waking with the phone's own network timeout, which
// `timeoutNotingFreezeAsync` cannot see. So the clock ticks beside the whole operation and is
// read at the moment it fails, whatever the error is.

/** How an operation run by `settleNotingFreezeAsync` ended. */
export enum SettledOutcome {
  Answered = 'answered',
  Failed = 'failed',
}

/** A failure, plus how long the page was not running while the operation was pending. */
export interface FailedNotingFreeze {
  outcome: SettledOutcome.Failed;
  error: unknown;
  notRunningMs: number;
  frozeWhilePending: boolean;
}

/** How `settleNotingFreezeAsync` ended: the value, or the error plus what the clock saw. */
export type SettledNotingFreeze<T> =
  | { outcome: SettledOutcome.Answered, value: T }
  | FailedNotingFreeze;

/**
 * Run `operation` with a running-time clock ticking beside it, and never reject: a failure comes
 * back as `SettledOutcome.Failed` with how long the page was not running while it was pending.
 * `frozeWhilePending` is true when that is more than one tick, the same threshold
 * `timeoutNotingFreezeAsync` uses. The clock only measures; it never ends the operation.
 */
export async function settleNotingFreezeAsync<T>(
  operation: () => Promise<T>,
  env: TimerEnvironment = realTimerEnvironment(),
): Promise<SettledNotingFreeze<T>> {
  const ticks = startRunningClockTicks(env);
  try {
    return { outcome: SettledOutcome.Answered, value: await operation() };
  } catch (error) {
    // stop() reads now: on waking, the failure can arrive before the overdue tick does
    ticks.stop();
    const notRunningMs = ticks.clock.notRunningMs();
    return { outcome: SettledOutcome.Failed, error, notRunningMs, frozeWhilePending: notRunningMs > RUNNING_TIME_TICK_MS };
  } finally {
    ticks.stop();
  }
}

/** The freeze facts of `err` when it is a wall-time limit that fired because of a freeze, else null. */
export function timeoutCausedByFreeze(err: unknown): TimeoutAfterFreeze | null {
  const facts = (err as TimeoutErrorWithRunningTime | null)?.runningTimeFacts;
  if (!facts || !facts.freezeCaused) return null;
  return { notRunningMs: facts.notRunningMs };
}
